// Phase 46 — Top-level React ErrorBoundary. Until this was added a thrown
// render error (e.g. "Objects are not valid as a React child" when a
// toast tried to render a raw Pydantic 422 detail array) blew up the
// whole tree into a white screen. The boundary now catches that class
// of failure, logs to the console, and shows a friendly recovery panel.
import React from "react";

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Keep the full stack in the console for ops debugging — never
    // surface raw internals to the user.
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary] caught:", error, info);
    this.setState({ info });
  }

  reset = () => {
    this.setState({ error: null, info: null });
  };

  reload = () => {
    try { window.location.reload(); } catch { this.reset(); }
  };

  render() {
    if (this.state.error) {
      const message = (() => {
        const e = this.state.error;
        if (!e) return "Unknown error";
        if (typeof e === "string") return e;
        if (typeof e?.message === "string") return e.message;
        try { return JSON.stringify(e); } catch { return "Unknown error"; }
      })();
      return (
        <div
          className="min-h-screen flex items-center justify-center p-6 bg-background"
          data-testid="app-error-boundary"
        >
          <div className="max-w-md w-full bg-card border border-border rounded-[10px] p-6 space-y-4">
            <div className="mono text-[10px] uppercase tracking-[0.3em] text-rose-600">
              // Something went wrong
            </div>
            <h1 className="text-2xl font-extrabold tracking-tight">
              We hit an unexpected error.
            </h1>
            <p className="text-sm text-muted-foreground">
              The interface failed to render. Your data is safe — this is a
              display issue. Reload the page to recover.
            </p>
            <pre
              className="bg-secondary/60 border border-border rounded-md p-3 text-[11px] font-mono whitespace-pre-wrap break-all max-h-[160px] overflow-auto"
              data-testid="app-error-boundary-message"
            >
              {message}
            </pre>
            <div className="flex items-center gap-2 pt-1">
              <button
                type="button"
                onClick={this.reload}
                className="btn-cta inline-flex items-center justify-center gap-2 text-sm font-bold rounded-md px-4 py-2"
                data-testid="app-error-boundary-reload"
              >
                Reload
              </button>
              <button
                type="button"
                onClick={this.reset}
                className="text-sm font-semibold rounded-md px-4 py-2 border border-border hover:bg-secondary transition-colors"
                data-testid="app-error-boundary-retry"
              >
                Try again
              </button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
